import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/empty-state";
import { useChatMessages, useCreateChatSession, useSendChatMessage } from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import { formatRelative } from "@/lib/utils";

const STORAGE_KEY = "oc-chat-tester-session";

export function ChatTesterPage() {
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const create = useCreateChatSession();
  const send = useSendChatMessage();
  const { data: messages, isLoading } = useChatMessages(sessionId ?? undefined);
  const toast = useToast();
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("Sales test session");
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const newSession = async () => {
    try {
      const session = await create.mutateAsync({ title });
      setSessionId(session.id);
      try {
        localStorage.setItem(STORAGE_KEY, session.id);
      } catch {
        // ignore
      }
      toast.success("Session created");
    } catch (err) {
      toast.error("Couldn't create session", err instanceof Error ? err.message : undefined);
    }
  };

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !body.trim()) return;
    const text = body.trim();
    setBody("");
    try {
      await send.mutateAsync({ sessionId, body: text });
    } catch (err) {
      toast.error("Send failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <PageShell
      title="Chat tester"
      description="Run prompts against the live agent. Use it before pushing instruction changes."
    >
      <div className="grid gap-5 lg:grid-cols-[320px,1fr]">
        <aside className="space-y-4">
          <section className="rounded-lg border border-line bg-surface p-5">
            <h3 className="text-md font-semibold text-ink">Session</h3>
            <p className="mt-1 text-sm text-ink-muted">
              Sessions are scoped to the configured agent. New sessions start fresh.
            </p>
            <div className="mt-4 space-y-3">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Session title"
              />
              <Button
                variant="primary"
                className="w-full"
                onClick={newSession}
                disabled={create.isPending}
              >
                Start new session
              </Button>
              {sessionId && (
                <p className="text-xs text-ink-faint">
                  Active: <span className="font-mono">{sessionId.slice(0, 8)}…</span>
                </p>
              )}
            </div>
          </section>
        </aside>

        <section className="flex flex-col rounded-lg border border-line bg-surface">
          <header className="flex items-center justify-between border-b border-line px-5 py-3">
            <h3 className="text-md font-semibold text-ink">Conversation</h3>
            {sessionId && messages && (
              <span className="text-xs text-ink-faint">{messages.length} messages</span>
            )}
          </header>

          <div ref={logRef} className="flex-1 min-h-[420px] max-h-[60vh] overflow-y-auto p-5 scrollbar-thin">
            {!sessionId ? (
              <EmptyState
                icon={MessageSquare}
                title="Start a session to begin"
                description="Hit 'Start new session' on the left to open a fresh conversation."
              />
            ) : isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-2/3" />
                ))}
              </div>
            ) : !messages || messages.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title="No messages yet"
                description="Send the first message below to start the test."
              />
            ) : (
              <ol className="space-y-2.5">
                {messages.map((m) => {
                  const isUser = m.role === "user";
                  return (
                    <li key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-md px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                          isUser ? "bg-accent-soft text-ink" : "border border-line bg-surface text-ink"
                        }`}
                      >
                        <div className="mb-1 text-xs uppercase tracking-wider text-ink-faint">
                          {m.role} · {formatRelative(m.created_at)}
                        </div>
                        {m.body}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {sessionId && (
            <form
              onSubmit={onSend}
              className="flex flex-col gap-2 border-t border-line p-4"
            >
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Send a message to the bot..."
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void onSend(e as unknown as React.FormEvent);
                  }
                }}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-ink-faint">⌘/Ctrl + Enter to send</p>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={send.isPending || !body.trim()}
                >
                  <Send className="h-4 w-4" />
                  Send
                </Button>
              </div>
            </form>
          )}
        </section>
      </div>
    </PageShell>
  );
}
